
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Shield, Loader2, Wifi, TestTube } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNetworkStatus } from '@/hooks/useNetworkConnection';

interface StoreStationGuardProps {
  children: React.ReactNode;
  onBack: () => void;
}

export const StoreStationGuard = ({ children, onBack }: StoreStationGuardProps) => {
  const [locationGranted, setLocationGranted] = useState(false);
  const [isCheckingLocation, setIsCheckingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const { toast } = useToast();
  const { isOnline } = useNetworkStatus();
  // Mock network status functionality for compatibility
  const networkStatus: "verified" | "failed" | "checking" = isOnline ? "verified" : "failed";
  const isTestingMode = false;
  const checkNetworkStatus = () => {};
  const enableTestingMode = () => {};

  useEffect(() => {
    checkNetworkStatus();
  }, [checkNetworkStatus]);

  // If testing mode is active, bypass location requirement
  useEffect(() => {
    if (isTestingMode) {
      setLocationGranted(true);
      console.log('Testing mode active - bypassing location requirements');
    }
  }, [isTestingMode]);

  const requestLocationAccess = async () => {
    setIsCheckingLocation(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      const errorMessage = "Geolocation is not supported by this browser.";
      setLocationError(errorMessage);
      toast({
        title: "Geolocation Not Supported",
        description: errorMessage,
        variant: "destructive"
      });
      setIsCheckingLocation(false);
      return;
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(pos),
          (err) => reject(err),
          {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 600000
          }
        );
      });

      console.log('Location verified for Store Station');
      setLocationGranted(true);
      
      toast({
        title: "Location Verified",
        description: "Access granted to Store Station.",
      });

    } catch (error: any) {
      console.error('Location access failed:', error);
      
      let errorMessage = "Unable to access location.";
      if (error.code === 1) {
        errorMessage = "Location access was denied. Please enable location permissions.";
      } else if (error.code === 2) {
        errorMessage = "Location information is unavailable.";
      } else if (error.code === 3) {
        errorMessage = "Location request timed out.";
      }
      
      setLocationError(errorMessage);
      toast({
        title: "Location Access Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsCheckingLocation(false);
    }
  };

  // Check if both location and network are verified (or testing mode is active)
  const isFullyVerified = (locationGranted && networkStatus === "verified") || isTestingMode;

  if (!isFullyVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Shield className="w-16 h-16 text-blue-600" />
            </div>
            <CardTitle className="text-xl text-blue-800">
              {isTestingMode ? "Testing Mode Active" : "Store Station Access"}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <div>
              <p className="text-gray-700 mb-4">
                {isTestingMode 
                  ? "Testing mode is active. All restrictions are bypassed." 
                  : "Store Station requires both location and network verification."
                }
              </p>
            </div>

            {isTestingMode && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center justify-center space-x-2">
                  <TestTube className="w-4 h-4 text-yellow-600" />
                  <p className="text-yellow-800 text-sm font-medium">
                    Testing Mode: All restrictions bypassed
                  </p>
                </div>
              </div>
            )}

            {/* Network Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <Wifi className={`w-5 h-5 ${
                  networkStatus === "verified" ? "text-green-500" : 
                  networkStatus === "failed" ? "text-red-500" : "text-yellow-500"
                }`} />
                <span className="font-medium">Network</span>
              </div>
              <span className={`text-sm ${
                networkStatus === "verified" ? "text-green-600" : 
                networkStatus === "failed" ? "text-red-600" : "text-yellow-600"
              }`}>
                {networkStatus === "verified" ? "Verified" : 
                 networkStatus === "failed" ? "Failed" : "Checking..."}
              </span>
            </div>

            {/* Location Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <MapPin className={`w-5 h-5 ${
                  locationGranted ? "text-green-500" : "text-red-500"
                }`} />
                <span className="font-medium">Location</span>
              </div>
              <span className={`text-sm ${
                locationGranted ? "text-green-600" : "text-red-600"
              }`}>
                {locationGranted ? "Verified" : isTestingMode ? "Bypassed" : "Required"}
              </span>
            </div>

            {locationError && !isTestingMode && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{locationError}</p>
              </div>
            )}

            {networkStatus === "failed" && !isTestingMode && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">
                  You must be connected to the store network to access Store Station.
                </p>
                <Button 
                  onClick={enableTestingMode} 
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full"
                >
                  <TestTube className="w-4 h-4 mr-2" />
                  Enable Testing Mode
                </Button>
              </div>
            )}

            {!locationGranted && !isTestingMode && (
              <Button 
                onClick={requestLocationAccess} 
                className="w-full" 
                disabled={isCheckingLocation}
              >
                {isCheckingLocation ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking Location...
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4 mr-2" />
                    Grant Location Access
                  </>
                )}
              </Button>
            )}

            <Button 
              onClick={onBack} 
              variant="outline"
              className="w-full"
            >
              Back to Main
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};
